import React from 'react';
import useGameStore from '../engine/store';

function formatMoney(value) {
  if (Math.abs(value) >= 1e9) return `$${(value / 1e9).toFixed(2)}B`;
  if (Math.abs(value) >= 1e6) return `$${(value / 1e6).toFixed(2)}M`;
  if (Math.abs(value) >= 1e3) return `$${(value / 1e3).toFixed(1)}K`;
  return `$${value.toFixed(0)}`;
}

export default function MonthResults() {
  const { lastMonthResults, month, continueToReview, bankHealth } = useGameStore();

  if (!lastMonthResults) return null;

  const r = lastMonthResults;
  const profit = r.profit;
  const isProfitable = profit >= 0;

  return (
    <div className="max-w-2xl mx-auto p-4 space-y-4 animate-slide-up">
      {/* Header */}
      <div className="text-center mb-2">
        <div className="text-3xl mb-1">{isProfitable ? '📈' : '📉'}</div>
        <h2 className="text-2xl font-black text-white">Month {month - 1} Results</h2>
      </div>

      {/* P&L Summary */}
      <div className="glass-panel p-5">
        <div className="grid grid-cols-3 gap-4 text-center">
          <div>
            <div className="text-xs text-gray-500 mb-1">Interest Income</div>
            <div className="text-xl font-black font-mono text-neon-green">+{formatMoney(r.totalInterestIncome)}</div>
          </div>
          <div>
            <div className="text-xs text-gray-500 mb-1">Default Losses</div>
            <div className="text-xl font-black font-mono text-neon-red">-{formatMoney(r.defaultLosses)}</div>
          </div>
          <div>
            <div className="text-xs text-gray-500 mb-1">Net Profit</div>
            <div className={`text-xl font-black font-mono ${isProfitable ? 'text-neon-green' : 'text-neon-red'}`}>
              {isProfitable ? '+' : ''}{formatMoney(profit)}
            </div>
          </div>
        </div>
      </div>

      {/* Loans funded */}
      {r.fundedLoans.length > 0 && (
        <div className="glass-panel p-4">
          <div className="text-xs text-gray-500 uppercase tracking-wider mb-3">
            📋 {r.fundedLoans.length} Loan{r.fundedLoans.length > 1 ? 's' : ''} Funded
          </div>
          <div className="space-y-2">
            {r.fundedLoans.map(loan => (
              <div key={loan.id} className="flex items-center justify-between text-sm p-2 rounded bg-dark-800/40">
                <div className="flex items-center gap-2">
                  <span>{loan.borrowerEmoji}</span>
                  <span className="text-gray-300">{loan.borrowerName}</span>
                </div>
                <div className="flex items-center gap-3">
                  <span className="font-mono text-white">{formatMoney(loan.amount)}</span>
                  <span className="font-mono text-neon-yellow text-xs">{(loan.playerRate * 100).toFixed(1)}%</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Defaults */}
      {r.defaults.length > 0 && (
        <div className="glass-panel p-4 border border-neon-red/20">
          <div className="text-xs text-neon-red uppercase tracking-wider mb-3">
            💀 {r.defaults.length} Default{r.defaults.length > 1 ? 's' : ''}
          </div>
          <div className="space-y-2">
            {r.defaults.map(loan => (
              <div key={loan.id} className="flex items-center justify-between text-sm p-2 rounded bg-red-900/10">
                <div className="flex items-center gap-2">
                  <span>{loan.borrowerEmoji}</span>
                  <span className="text-gray-300">{loan.borrowerName}</span>
                </div>
                <div className="text-right">
                  <div className="font-mono text-neon-red text-xs">Loss: {formatMoney(loan.loss)}</div>
                  <div className="font-mono text-gray-500 text-xs">Recovered: {formatMoney(loan.recovered)}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Completed loans */}
      {r.completedLoans.length > 0 && (
        <div className="glass-panel p-4 border border-neon-green/20">
          <div className="text-xs text-neon-green uppercase tracking-wider mb-3">
            ✅ {r.completedLoans.length} Loan{r.completedLoans.length > 1 ? 's' : ''} Fully Repaid
          </div>
          <div className="space-y-1">
            {r.completedLoans.map(loan => (
              <div key={loan.id} className="flex items-center justify-between text-sm p-1">
                <span className="text-gray-400">{loan.borrowerEmoji} {loan.borrowerName}</span>
                <span className="font-mono text-neon-green text-xs">{formatMoney(loan.originalAmount)}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* New events */}
      {r.newEvents.length > 0 && (
        <div className="glass-panel p-4">
          <div className="text-xs text-gray-500 uppercase tracking-wider mb-3">⚡ New Events</div>
          {r.newEvents.map((event, i) => (
            <div key={i} className="text-sm text-gray-300 p-1">{event.title}</div>
          ))}
        </div>
      )}

      {/* Continue */}
      <div className="text-center pt-2">
        <button onClick={continueToReview} className="btn-primary text-lg px-10 py-4">
          📋 Review Next Month's Loans →
        </button>
      </div>
    </div>
  );
}
